import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { articleList } from "@/mocks/articles";

export default function Footer() {
  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      form.reset();
      const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (btn) {
        btn.textContent = 'Subscribed!';
        btn.disabled = true;
      }
      return;
    }
    const formData = new FormData(form);
    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.textContent = 'Sending...';
      submitBtn.disabled = true;
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(() => {
        form.reset();
        if (submitBtn) {
          submitBtn.textContent = 'Subscribed!';
        }
      })
      .catch(() => {
        if (submitBtn) {
          submitBtn.textContent = 'Try Again';
          submitBtn.disabled = false;
        }
      });
  };

  return (
    <footer className="bg-foreground-900 text-white mt-16 md:mt-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-10 md:pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          <div>
            <h3 className="text-white font-heading text-lg font-semibold mb-4">Start with ACBuySpreadsheet</h3>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6">
              The most efficient spreadsheet-based buying and order tracking system designed for speed, accuracy, and simplified purchasing workflow.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </span>
                acbuy spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-line"></i>
                </span>
                buy it now
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-heading text-sm font-semibold mb-4 uppercase tracking-wider">Categories</h4>
            <ul className="flex flex-col gap-2.5">
              {categories.slice(0, 7).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-heading text-sm font-semibold mb-4 uppercase tracking-wider">SEO Guides</h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to="/acbuyspreadsheet-guide"
                  className="text-sm text-foreground-300 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Complete Guide (2026)
                </Link>
              </li>
              {articleList.slice(0, 5).map((article) => (
                <li key={article.slug}>
                  <Link
                    to={`/article/${article.slug}`}
                    className="text-sm text-foreground-300 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {article.title.length > 38 ? article.title.substring(0, 38) + '...' : article.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/"
                  className="text-sm text-foreground-300 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View All Guides &rarr;
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-heading text-sm font-semibold mb-4 uppercase tracking-wider">Stay Updated</h4>
            <p className="text-foreground-300 text-sm leading-relaxed mb-4">
              Get the latest updates on spreadsheet buying tools and order tracking optimization.
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d8trih4al24muhn2rajg"
              method="POST"
              onSubmit={handleNewsletterSubmit}
            >
              <div className="flex gap-0">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-3 bg-foreground-800 border border-foreground-700 rounded-l-full text-sm text-white placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  className="w-12 h-12 flex items-center justify-center bg-primary-500 hover:bg-primary-600 rounded-r-full cursor-pointer transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-white text-lg"></i>
                  </span>
                </button>
              </div>
              <div className="honeypot-wrapper">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>
            </form>
          </div>
        </div>

        <div className="mt-14 md:mt-16 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-400 text-xs">
            &copy; {new Date().getFullYear()} ACBuySpreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-6 flex-wrap">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-foreground-400 text-xs hover:text-primary-300 transition-colors cursor-pointer">
              Main Store
            </a>
            <Link to="/" className="text-foreground-400 text-xs hover:text-primary-300 transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/acbuyspreadsheet-guide" className="text-foreground-400 text-xs hover:text-primary-300 transition-colors cursor-pointer">
              Shopping Guide
            </Link>
          </div>
        </div>

        <div className="mt-8 text-center opacity-[0.04] select-none pointer-events-none">
          <span className="text-[120px] font-heading font-bold leading-none">ACBUYSPREADSHEET</span>
        </div>
      </div>
    </footer>
  );
}